import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Gauge, RefreshCw, Inbox, Hourglass, AlarmClock, BellRing, FileWarning, PenLine, Lightbulb, Trophy, Smartphone } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Tile = ({ to, icon: Icon, label, value, hint, tone = 'slate', testId, children }) => {
  const tones = {
    red: 'border-rose-200 bg-rose-50 text-rose-700', amber: 'border-amber-200 bg-amber-50 text-amber-700',
    blue: 'border-sky-200 bg-sky-50 text-sky-700', green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    slate: 'border-slate-200 bg-white text-slate-700',
  };
  const t = value > 0 ? tones[tone] : tones.slate;
  return (
    <Link to={to} className={`group rounded-2xl border p-4 flex flex-col gap-2 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md ${t}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5"><Icon className="w-4 h-4" />{label}</span>
      </div>
      <span className="text-3xl font-bold font-[Outfit]" data-testid={`${testId}-value`}>{value}</span>
      {hint && <span className="text-xs opacity-80">{hint}</span>}
      {children}
    </Link>
  );
};

export default function CommandCenterPage() {
  const { user } = useAuth();
  const allowed = ['super_admin', 'gestor'].includes(user?.role);
  const [d, setD] = useState(null);
  const load = useCallback(() => { axios.get(`${API_URL}/command-center`).then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  useEffect(() => { if (allowed) { load(); const t = setInterval(load, 120000); return () => clearInterval(t); } return undefined; }, [allowed, load]);

  if (!allowed) return <Layout><p className="p-6 text-sm text-slate-500">Sem permissão para ver esta página.</p></Layout>;
  const c = d?.contratos || {};
  return (
    <Layout>
      <div className="space-y-6" data-testid="command-center-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-[Outfit] text-[#2D3E6F] flex items-center gap-2"><Gauge className="w-7 h-7" />Centro de Comando</h1>
            <p className="text-sm text-slate-500 mt-1">Tudo o que precisa de atenção hoje. Clique num bloco para ver o detalhe.</p>
          </div>
          <button type="button" onClick={load} className="text-sm text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 flex items-center gap-1 hover:bg-slate-50" data-testid="cc-refresh"><RefreshCw className="w-3.5 h-3.5" />Atualizar</button>
        </div>
        {!d ? <p className="text-sm text-slate-400">A carregar…</p> : (
          <>
            <section>
              <h2 className="text-base md:text-lg font-semibold text-slate-800 mb-3">Leads</h2>
              <div className="cc-grid gap-3">
                <Tile to="/leads/gestao" icon={Inbox} label="Por atribuir" value={d.leads.por_atribuir} tone="red" testId="cc-por-atribuir" />
                <Tile to="/pendencias?tab=nova72" icon={Hourglass} label="Nova +72h" value={d.nova72.total} tone="red" testId="cc-nova72"
                  hint={d.nova72.por_comercial.slice(0, 3).map((r) => `${r.name} (${r.n})`).join(', ') || null} />
                <Tile to="/leads/gestao" icon={AlarmClock} label="Contactos atrasados" value={d.leads.atrasadas} tone="amber" testId="cc-atrasadas" />
                <Tile to="/leads/gestao" icon={Hourglass} label="Paradas" value={d.leads.paradas} tone="amber" testId="cc-paradas" />
                <Tile to="/leads/gestao" icon={Hourglass} label="Expiradas" value={d.leads.expiradas} tone="red" testId="cc-expiradas" />
              </div>
            </section>
            <section>
              <h2 className="text-base md:text-lg font-semibold text-slate-800 mb-3">Contratos e clientes</h2>
              <div className="cc-grid gap-3">
                <Tile to="/pendencias" icon={BellRing} label="Pendências abertas" value={d.pendencias} tone="amber" testId="cc-pendencias" />
                <Tile to="/contracts/all" icon={FileWarning} label="Por validar" value={(c.validacao_documentos || 0) + (c.pendente_documentos || 0)} tone="blue" testId="cc-por-validar"
                  hint={`${c.validacao_documentos || 0} validação · ${c.pendente_documentos || 0} pend. documentos`} />
                <Tile to="/contracts/all" icon={FileWarning} label="Esclarecimentos" value={(c.esclarecimentos || 0) + (c.esclarecimentos_respondidos || 0)} tone="amber" testId="cc-esclarecimentos"
                  hint={`${c.esclarecimentos_respondidos || 0} respondidos por validar`} />
                <Tile to="/leads/pipeline" icon={PenLine} label="Assinaturas por concluir" value={d.assinaturas.pendentes} tone="blue" testId="cc-assinaturas"
                  hint={`${d.assinaturas.mais_24h} há mais de 24h`} />
                <Tile to="/oportunidades" icon={Lightbulb} label="Oportunidades" value={d.oportunidades.por_contactar} tone="green" testId="cc-oportunidades"
                  hint={`${d.oportunidades.revisoes} revisões "Sem troca" · ${d.oportunidades.acompanhamento} em acompanhamento`} />
              </div>
            </section>
            <section className="grid lg:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="cc-ranking">
                <h2 className="text-base md:text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Ranking de hoje</h2>
                {!d.ranking.length ? <p className="text-sm text-slate-400">Ainda sem atividade hoje.</p> : (
                  <ol className="divide-y divide-slate-100">
                    {d.ranking.map((r, i) => (
                      <li key={r.id} className="flex items-center justify-between py-2 text-sm" data-testid={`cc-rank-${i}`}>
                        <span><b className="text-slate-400 mr-2">{i + 1}.</b>{r.name}</span>
                        <span className="text-slate-600">{r.contratos} contrato(s) · {r.contactos} contacto(s)</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              <Link to="/avisos-telefone" className="rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow" data-testid="cc-sem-push">
                <h2 className="text-base md:text-lg font-semibold text-slate-800 mb-2 flex items-center gap-2"><Smartphone className="w-4 h-4 text-rose-500" />Comerciais sem avisos no telefone: {d.sem_push.total}</h2>
                <p className="text-sm text-slate-600">{d.sem_push.nomes.join(', ') || 'Todos ativos 🎉'}</p>
              </Link>
            </section>
          </>
        )}
      </div>
    </Layout>
  );
}
